import {
  Button,
  Card,
  Grid,
  Typography,
  styled,
  Dialog as MuiDialog,
  Button as MuiButton,
  Tooltip,
} from "@mui/material";

import { TableSchema } from "../../components/TableView";
import { useContext, useEffect, useState } from "react";
import { useAxios } from "../../services/http.service";
import ApiRoutes from "../../config/api.routes";
import { LandToken } from "../../types/LandToken";
import { useProfileDataCache } from "../../contexts/ProfileDataCache";
// import Dialog from "../../components/Dialog/Dialog";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router";
import { baseLandsSchema } from "../../utils/table-schemas";
import { AppRoutes } from "../../config";
import { sizing } from "../../utils/methods";
import { EmptyBox } from "./Guarding";
import TableComponent from "../../components/TableView/TableComponent";
import { Text } from "../../components/Typography";
import InfoIconOutlined from "@mui/icons-material/InfoOutlined";
import moment from "moment";
import { CurrencyLabel, PulseCircle } from "../../components";
// eslint-disable-next-line max-len
import {
  onVerification,
  onReassignment,
  EcoTokenStatus,
  DATE_FORMAT,
  VERIFICATION_INTERVAL_VALUE,
  VERIFICATION_INTERVAL_UNIT,
  TIME_DISPLAY_UNIT,
  CURRENCY_NAME,
} from "../../utils/constants";
import { formatValue, toHectares } from "../../utils/formatValue";
import { resolveStandardStatus } from "../../utils/standard-status";
import useAuthorization from "../../contexts/useAuthorization";
import useMetaMask from "../../contexts/useMetaMask";
import { getMetaMaskSigner, getReadOnlyContract } from "../../services/contract.service";
import { relayContractCall } from "../../services/relay.service";
import { fromWei } from "web3-utils";
import { Result } from "ethers";
import { AppContext } from "../../contexts/AppContext";

/**
 * Bounty for a reassignment row: the easement pot a takeover verifier inherits, read from
 * EcoFuturesCore.getEasement and shown in USD at the current mint rate. A fresh upload has
 * no easement yet, so those rows show `feeAmount` instead — see the Bounty column below.
 */
const EasementBountyCell = ({ token }: { token: LandToken }) => {
  const [bountyValue, setBountyValue] = useState<string>("Loading...");
  const appContext = useContext(AppContext);

  const rate = appContext?.mintRate;

  useEffect(() => {
    const fetchBounty = async () => {
      try {
        if (!token.etNft?.id) {
          setBountyValue("N/A");
          return;
        }

        const ecofuturesCoreContract = getReadOnlyContract("ecofutures-core");

        if (!ecofuturesCoreContract) {
          setBountyValue("N/A");
          return;
        }

        const reward: Result = await ecofuturesCoreContract.getEasement(token.etNft.id);
        const { easementBalance } = reward.toObject();
        const totalReward = formatValue(easementBalance, false);
        const bountyInUsd = totalReward * rate;

        if (bountyInUsd) {
          setBountyValue(formatValue(bountyInUsd, true));
        } else {
          setBountyValue("N/A");
        }
      } catch (error: any) {
        console.log(`Bounty fetch error for token ${token.id}:`, error.message);
        // If reward hasn't been set yet, show N/A
        setBountyValue("N/A");
      }
    };

    fetchBounty();
  }, [token]);

  const resolved = bountyValue !== "N/A" && bountyValue !== "Loading...";

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Text medium sx={{ gap: 3 }}>
        <Text medium contrast={resolved} mono sx={{ color: bountyValue === "N/A" ? "text.disabled" : "text.primary" }}>
          {bountyValue}
        </Text>
        {resolved && (
          <Text capital medium mono sx={{ color: "text.disabled" }}>
            USD
          </Text>
        )}
      </Text>
    </div>
  );
};

/**
 * Whether a /trust/tokens row belongs in the Uploads table. Exported so the tab badge in
 * Verifications counts exactly the rows the table shows — the endpoint returns every token
 * bound to this wallet, most of which are not actionable here.
 *
 * Reassignment rows (a verifier walked away, or the trust admin re-routed the land) share this
 * table with fresh uploads since the Unassigned tab was folded in — the endpoint already returns
 * both.
 *
 * APPROVED rows never belong here, challenged or not. /trust/tokens itself never returns them;
 * the only APPROVED rows in this cache come from the merged /trust/tokens/available, which is
 * other orgs' assessments — the pool an independent verifier challenges from. Treating a
 * challenged one as an upload offered the CHALLENGER a "Respond" on their own challenge. The
 * verifier's response lives on the Verified tab, the list of lands they actually scored.
 */
export const isUploadRow = (token: LandToken, country?: string | null): boolean => {
  const uploadEligible = token.status === EcoTokenStatus.VERIFICATION_REQUIRED || onReassignment(token.status);
  if (!uploadEligible) {
    return false;
  }

  // Only show lands in the same country as the user
  if (!token.meta?.location || !country) {
    return false;
  }

  // Handle case where meta.location is a stringified JSON object
  let location: any = token.meta.location;
  if (typeof location === "string") {
    try {
      location = JSON.parse(location);
    } catch (e) {
      console.error("Failed to parse location for token:", token.id, e);
      return false;
    }
  }

  return location?.country === country;
};

const TrustLands = () => {
  const navigate = useNavigate();
  const axios = useAxios();
  const { palette } = useTheme();
  const { getCachedData, refreshProfileData } = useProfileDataCache();
  const [isLoading, setLoading] = useState(true);
  const [isClaiming, setClaiming] = useState(false);
  const [isClaimingReassign, setClaimingReassign] = useState(false);
  const [isRequestLoading, setRequestLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLand, setSelectedLand] = useState<LandToken | null>(null);
  const { user } = useAuthorization();
  const [reassignLandIds, setReassignLandIds] = useState<number[]>([]);
  const hasTrustAdminReassignedLands = user?.pendingReassignment?.hasPendingReassignment;

  const { signer } = useMetaMask();

  useEffect(() => {
    setReassignLandIds([]);
    if (hasTrustAdminReassignedLands) {
      const reassignedIds = user?.pendingReassignment?.reassignments;
      reassignedIds?.map((item) => {
        setReassignLandIds((prevState) => [...prevState, item.tokenId]);
      });
    }
  }, [user]);

  useEffect(() => {
    console.log(reassignLandIds);
  }, [reassignLandIds]);

  // Get cached trust tokens data and filter by user's country. Reassignments addressed to this
  // wallet sort to the top.
  const allTokens = getCachedData("trustTokens") || [];
  const tokens = allTokens
    .filter((token: LandToken) => isUploadRow(token, user?.country))
    .sort((a: LandToken, b: LandToken) => {
      const aIsReassigned = reassignLandIds.includes(a.id);
      const bIsReassigned = reassignLandIds.includes(b.id);

      if (aIsReassigned && !bIsReassigned) {
        return -1;
      }
      if (!aIsReassigned && bIsReassigned) {
        return 1;
      }
      return 0;
    });

  useEffect(() => {
    console.log("TrustLandsNew - Filtered tokens:", tokens.length, "out of", allTokens.length);
  }, [tokens, allTokens]);

  // The attestation queue used to be derived here from the full eco-token list. It now lives
  // in the portal's Review tab (ReviewLands), backed by GET /trust/tokens/attestation — the
  // client cannot evaluate EcoBank's independence rule, which is defined over the on-chain
  // orgId rather than anything served on the token.
  const waitingVerificationSchema: TableSchema<LandToken>[] = [
    ...baseLandsSchema,
    {
      id: "period",
      label: "Term / Size",
      sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
      render: (_data: LandToken) => {
        // A fresh upload shows the full requested term. A reassignment row is an easement
        // already running, so what the incoming verifier signs up for is the REMAINING term.
        let period: number | string = _data.meta?.contractPeriod ?? "";

        if (
          onReassignment(_data.status) &&
          _data.etNft?.easementActivated?.contractStart &&
          _data.meta?.contractPeriod
        ) {
          const contractStart = moment(_data.etNft.easementActivated.contractStart);
          // contractPeriod is in TIME_DISPLAY_UNIT — days on the accelerated testnet
          // (1 day = 1 contract year), years in production. Same unit as the label below.
          const contractEnd = contractStart.clone().add(_data.meta.contractPeriod, TIME_DISPLAY_UNIT);
          const now = moment();

          if (now.isBefore(contractEnd)) {
            // Remaining term in that same unit, rounded up to the nearest 0.5
            period = Math.ceil(contractEnd.diff(now, TIME_DISPLAY_UNIT, true) * 2) / 2;
          } else {
            period = 0; // Contract expired
          }
        }

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text medium sx={{ gap: 3 }}>
              <Text medium contrast mono>
                {period}
              </Text>
              <Text capital medium mono sx={{ color: "text.disabled" }}>
                {TIME_DISPLAY_UNIT}
              </Text>
            </Text>
            <Text medium sx={{ gap: 3 }}>
              <Text medium mono contrast>
                {toHectares(_data?.meta?.size || "").replace(/ hectares$/, "")}
              </Text>
              <Text capital medium mono sx={{ color: "text.disabled" }}>
                hectares
              </Text>
            </Text>
          </div>
        );
      },
    },
    {
      id: "contact",
      label: "Contact",
      sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
      render: (_data: LandToken) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {_data.meta?.additional_contacts?.map((item, index) => (
            <div key={index}>{item}</div>
          ))}
        </div>
      ),
    },
    {
      id: "bounty",
      label: "Bounty",
      style: { width: "170px" },
      render: (data) => {
        // Reassignment: the on-chain easement pot the takeover verifier inherits. Fresh upload:
        // the verification fee the guardian posted with the request (feeAmount, in wei).
        if (onReassignment(data.status)) {
          return <EasementBountyCell token={data} />;
        }
        const fee = fromWei(Number(data?.feeAmount), "ether");
        const formattedFee = formatValue(Number(fee), true, 2);
        return (
          <Text numeric contrast medium>
            {formattedFee ? formattedFee : "-"}
            <CurrencyLabel currency={CURRENCY_NAME} />
          </Text>
        );
      },
    },
    {
      id: "status",
      label: "Status",
      style: { width: "170px" },
      render: (data) => {
        const lastVrf = data.verifications?.[data.verifications?.length - 1];
        const standardized = resolveStandardStatus(
          {
            status: data.status,
            etNft: data.etNft,
            meta: data.meta,
            offerStatus: data.offerStatus,
          },
          {
            role: "trust",
            lastVerificationStatus: (lastVrf?.status as EcoTokenStatus) || null,
            approvedLabel: "Approved",
          }
        );
        if (standardized) {
          return (
            <Text
              medium
              contrast
              capital
              sx={{ color: standardized.color, p: 0, display: "flex", alignItems: "center" }}
            >
              {standardized.showPulse && (
                <PulseCircle
                  warning={!!standardized.pulseWarning}
                  sold={!!standardized.pulseSold && !standardized.pulseWarning}
                />
              )}
              {standardized.label}
            </Text>
          );
        }

        const verification = (lastVrf && onVerification(lastVrf.status)) || onVerification(data.status);
        const isReassignment = (lastVrf && onReassignment(lastVrf.status)) || onReassignment(data.status);
        const reVerification = lastVrf?.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED;
        const claimed = lastVrf?.status === EcoTokenStatus.CLAIMED;
        const verificationStarted =
          lastVrf?.createdAt && moment(lastVrf.createdAt).add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT);
        const sold =
          !!data.etNft?.ownerAddress && !!data.etNft?.minterAddress && data.etNft.ownerAddress !== data.minterAddress;
        const minted = data.status === EcoTokenStatus.VERIFIED;
        const approved = data.status === EcoTokenStatus.APPROVED;
        const expired =
          sold && data.etNft?.easementActivated?.contractStart && data.meta?.contractPeriod
            ? moment(data.etNft.easementActivated.contractStart)
                .add(data.meta.contractPeriod, TIME_DISPLAY_UNIT)
                .isBefore()
            : false;
        const remainingTrees =
          formatValue(data.etNft?.easementActivated?.treeTotal as any, false) -
          (formatValue(data.etNft?.easementActivated?.rewardClaimed as any, false) || 0);
        const isTreesDepleted = sold && expired && remainingTrees <= 0;
        const textColor =
          !lastVrf && data.status === EcoTokenStatus.VERIFICATION_REQUIRED
            ? palette.primary.main
            : lastVrf?.status === EcoTokenStatus.DRAFT
            ? palette.error.main
            : lastVrf?.status === EcoTokenStatus.CLAIMED
            ? palette.success.main
            : lastVrf && onVerification(lastVrf.status)
            ? palette.secondary.main
            : lastVrf?.status === EcoTokenStatus.APPROVED
            ? sold
              ? palette.success.main
              : palette.primary.main
            : palette.secondary.main;

        const active =
          minted && data.etNft?.easementActivated?.contractStart
            ? moment(data.etNft.easementActivated.contractStart)
            : null;

        const text = isTreesDepleted ? (
          "Expired"
        ) : isReassignment ? (
          "Reassignment"
        ) : verification ? (
          <>
            <PulseCircle />
            {reVerification ? "Required" : claimed ? "Claimed" : "Verification"}
          </>
        ) : sold ? (
          <>
            <PulseCircle sold />
            Active
          </>
        ) : lastVrf?.status === EcoTokenStatus.REJECTED ? (
          "Rejected"
        ) : lastVrf?.status === EcoTokenStatus.VERIFICATION_REQUIRED ? (
          "Uploaded"
        ) : !lastVrf && data.status === EcoTokenStatus.VERIFICATION_REQUIRED ? (
          "Uploaded"
        ) : approved ? (
          "Approved"
        ) : (
          "Verified"
        );

        const single = data.verifications?.length === 1;

        const last =
          minted && (active || sold) && lastVrf?.status === EcoTokenStatus.APPROVED
            ? data.timestamps?.verifiedAt
              ? moment(data.timestamps?.verifiedAt)
              : null
            : null;

        let nextVerification = null;

        if (single) {
          if (active) {
            nextVerification = active.add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT);
          } else if (sold) {
            nextVerification = moment(data.timestamps?.verifiedAt).add(
              VERIFICATION_INTERVAL_VALUE,
              VERIFICATION_INTERVAL_UNIT
            );
          }
        } else {
          if (last) {
            nextVerification = last.add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT);
          }
        }

        const soon = nextVerification && nextVerification.isBefore(moment().add(3, "h"));
        const pastDue = nextVerification && nextVerification.isBefore(moment());

        if (active) {
          console.log(`Contract started for [${data.id}] at ${moment(active).format(DATE_FORMAT)}`, {
            single,
            status: lastVrf?.status,
            token: data.status,
          });
        } else {
          console.log(`Contract not started for [${data.id}]`, {
            single,
            status: lastVrf?.status,
            token: data.status,
          });
        }

        const tenseText = pastDue ? "due" : "is";

        if (nextVerification) {
          console.log({ expired, reVerification, nextVerification, active });
        }

        return (
          <Tooltip
            arrow={true}
            placement="bottom"
            disableHoverListener={expired || (!reVerification && !nextVerification && !active)}
            title={
              nextVerification
                ? `Next verification ${tenseText} ` +
                  (pastDue ? moment(nextVerification).toNow() : moment(nextVerification).fromNow())
                : verificationStarted
                ? "Should be completed " + verificationStarted.fromNow()
                : false
            }
          >
            <span style={{ cursor: "pointer" }}>
              <Text
                mono
                sx={{ flexDirection: "column", color: pastDue ? "error.main" : soon ? "action.pulse" : "text.primary" }}
              >
                <Text medium contrast capital sx={{ color: textColor, p: 0, display: "flex", alignItems: "center" }}>
                  {text}
                </Text>
                {!expired && !verification && nextVerification && <Text>{nextVerification.format(DATE_FORMAT)}</Text>}
                {reVerification && verificationStarted && (
                  <Text sx={{ color: "text.disabled" }}>{verificationStarted.format(DATE_FORMAT)}</Text>
                )}
              </Text>
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      sx: { width: "180px" },
      render: (_data: LandToken) => {
        if (onReassignment(_data.status)) {
          return (
            <div style={{ display: "flex" }}>
              <TableActionButton onClick={() => handleClaimReassignClick(_data)} size="small" variant="contained">
                {reassignLandIds.includes(_data.id) ? "Accept" : "Opt In"}
              </TableActionButton>
            </div>
          );
        } else {
          return (
            <div style={{ display: "flex" }}>
              <TableActionButton
                onClick={() => handleClaimClick(_data)}
                size="small"
                variant="contained"
                sx={{ textTransform: "capitalize", "&:hover": { border: "none", outline: "none" } }}
              >
                Claim
              </TableActionButton>
            </div>
          );
        }
      },
    },
  ];

  const handleClaimClick = (data: LandToken) => {
    setError("");
    setClaiming(true);
    try {
      setSelectedLand(data);
    } catch (e: any) {
      console.log({ e });
      setError(e.message);
    }
  };

  const handleClaimReassignClick = (data: LandToken) => {
    setError("");
    setClaimingReassign(true);
    try {
      setSelectedLand(data);
    } catch (e: any) {
      console.log({ e });
      setError(e.message);
    }
  };

  const handleClaimRequest = async () => {
    setError("");
    setRequestLoading(true);

    try {
      if (!selectedLand) {
        throw new Error("Unexpected error, please close modal and select land again");
      }

      // Step 1: Call admin.acceptVerificationRequest(requestId) on-chain
      const requestId = Number(selectedLand.requestId);
      console.log("[handleClaimRequest] requestId:", requestId, "selectedLand:", selectedLand);

      if (!Number.isFinite(requestId)) {
        throw new Error("Missing or invalid requestId for this land");
      }

      const signerInstance = await getMetaMaskSigner();
      const signerAddress = await signerInstance.getAddress();

      // Surface the real revert reason before asking for a signature. The relayed call's
      // _msgSender() is the verifier, so a read-only staticCall from the same address
      // simulates the sponsored call exactly.
      try {
        const adminRO = getReadOnlyContract("ecofutures-admin");
        await adminRO!.acceptVerificationRequest.staticCall(requestId, { from: signerAddress });
      } catch (simErr: any) {
        console.error("[handleClaimRequest] staticCall revert:", simErr);
        const reason =
          simErr?.revert?.args?.[0] ||
          simErr?.shortMessage ||
          simErr?.reason ||
          simErr?.info?.error?.message ||
          simErr?.message ||
          "Verification request cannot be accepted";
        throw new Error(reason);
      }

      // Gasless lane: one EIP-712 signature (no gas, no BNB) → backend /relay →
      // resolved only once VerificationAccepted is observed on-chain.
      const { txHash } = await relayContractCall(
        "ecofutures-admin",
        "acceptVerificationRequest",
        [requestId],
        "VerificationAccepted",
        "Accept verification request"
      );
      console.log("[handleClaimRequest] relayed acceptVerificationRequest tx:", txHash);

      // Step 3: POST to claim endpoint
      const claimRes = await axios.post(ApiRoutes.trust.claim, { id: selectedLand.id }).then((res) => res.data);
      console.log("[handleClaimRequest] claim response:", claimRes);

      navigate(AppRoutes.Verification.path.replace(":tokenId", selectedLand?.id as string));
    } catch (e: any) {
      console.log({ e });
      setError(
        e.response?.data?.status === 404
          ? "Land is already claimed by other trust account"
          : e.response?.data?.message || e.message || "Unexpected error, please try again later"
      );
    } finally {
      setClaiming(false);
      setSelectedLand(null);
      setRequestLoading(false);
      await refreshProfileData();
      // Cache will automatically refresh on next data fetch
    }
  };

  const handleClaimUnassigned = async () => {
    setError("");
    setRequestLoading(true);

    try {
      if (!selectedLand) {
        throw new Error("Unexpected error, please close modal and select land again");
      }
      const response = await axios
        .get(ApiRoutes.user.authString, { withCredentials: false })
        .then((res) => res)
        .catch((err) => err.response);

      if (!response || !response.data || !response?.status || (response?.status && response.status !== 200)) {
        throw new Error("Failed to get auth string from server");
        return;
      }

      const dataTs = response.data + Date.now();
      const signature = await signer(dataTs);
      if (signature) {
        const response = await axios
          .post(ApiRoutes.trust.claimUnassigned, {
            ecoTokenId: selectedLand.id as string,
            signature,
          })
          .then((res) => res.data);

        if (response) {
          setClaiming(false);
          setClaimingReassign(false);
          setSelectedLand(null);
          setRequestLoading(false);
        }
      }
    } catch (e: any) {
      console.log({ e });
      setError(
        e.response?.data?.status === 404
          ? "Land is already claimed by other trust account"
          : e.response?.data?.message || "Unexpected error, please try again later"
      );
    } finally {
      setClaiming(false);
      setClaimingReassign(false);
      setSelectedLand(null);
      setRequestLoading(false);
      // Cache will automatically refresh on next data fetch
    }
  };

  const handleClaimReassignRequest = async () => {
    setError("");
    setRequestLoading(true);

    try {
      if (!selectedLand) {
        throw new Error("Unexpected error, please close modal and select land again");
      }
      const response = await axios
        .get(ApiRoutes.user.authString, { withCredentials: false })
        .then((res) => res)
        .catch((err) => err.response);

      if (!response || !response.data || !response?.status || (response?.status && response.status !== 200)) {
        throw new Error("Failed to get auth string from server");
        return;
      }

      const dataTs = response.data + Date.now();
      const signature = await signer(dataTs);
      if (signature) {
        const response = await axios
          .post(ApiRoutes.trust.acceptReassign, {
            tokenId: selectedLand.id as string,
            signature,
          })
          .then((res) => res.data);

        if (response) {
          setClaiming(false);
          setSelectedLand(null);
          setRequestLoading(false);
          window.location.reload();
        }
      }
    } catch (e: any) {
      console.log({ e });
      setError(
        e.response?.data?.status === 404
          ? "Land is already claimed by other trust account"
          : e.response?.data?.message || "Unexpected error, please try again later"
      );
    } finally {
      setClaiming(false);
      setSelectedLand(null);
      setRequestLoading(false);
      // Cache will automatically refresh on next data fetch
    }
  };

  const handleRejectReassignRequest = async () => {
    setError("");
    setRequestLoading(true);

    try {
      if (!selectedLand) {
        throw new Error("Unexpected error, please close modal and select land again");
      }
      const response = await axios
        .post(ApiRoutes.trust.rejectReassign, { tokenId: String(selectedLand.id) })
        .then((res) => res.data);
      if (response) {
        setClaiming(false);
        setSelectedLand(null);
        setRequestLoading(false);
        window.location.reload();
      }
    } catch (e: any) {
      console.log({ e });
      setError(
        e.response?.data?.status === 404
          ? "Land is already claimed by other trust account"
          : e.response?.data?.message || "Unexpected error, please try again later"
      );
    } finally {
      setClaiming(false);
      setSelectedLand(null);
      setRequestLoading(false);
      // Cache will automatically refresh on next data fetch
    }
  };

  const handleClaimModalClose = () => {
    if (!isRequestLoading) {
      setClaiming(false);
      setSelectedLand(null);
    } else {
      setError("You cannot close this modal while claim request is pending!");
    }
  };
  const handleClaimReassignModalClose = () => {
    if (!isRequestLoading) {
      setClaimingReassign(false);
      setSelectedLand(null);
    } else {
      setError("You cannot close this modal while claim request is pending!");
    }
  };

  if (!isLoading && tokens.length === 0) {
    return (
      <Container>
        <EmptyBox sx={{ borderRadius: "20px", height: "60vh" }}>
          <span>There is nothing here yet!</span>
        </EmptyBox>
      </Container>
    );
  }

  return (
    <Container>
      {isLoading && (
        <EmptyBox sx={{ borderRadius: "20px", height: "60vh" }}>
          <span>🔎</span>
          <span>Data is loading...</span>
        </EmptyBox>
      )}
      <div
        style={{
          display: isLoading ? "none" : "block",
          width: "100%",
          minHeight: "57vh",
          borderRadius: "20px",
        }}
      >
        <TableComponent
          mobileTitle="Lands required verification"
          setLoading={setLoading}
          customData={tokens}
          apiURL=""
          schema={waitingVerificationSchema}
          totalItems={tokens.length}
          batch={5}
          hideTitle={true}
          title=""
        />
      </div>

      <DialogInfo open={isClaiming} onClose={() => handleClaimModalClose()} title={""}>
        <Wrapper
          sx={{
            p: 12,
            backgroundColor: "white",
            borderColor: ({ palette }) => palette.action.disabled,
          }}
        >
          <IconContainer>
            <CircularIcon>
              <InfoIconOutlined fontSize="large" color="primary" />
            </CircularIcon>
          </IconContainer>
          <InfoBody>
            <p style={{ fontWeight: 800, color: "black", alignSelf: "center", fontSize: "26px", marginTop: "16px" }}>
              Are you sure?
            </p>
            <p style={{ lineHeight: "1.5", textAlign: "center" }}>
              This action will lock your wallet to this land. By confirming, you accept to the role of Trustee to this
              land and the responsibilities it implies.
              <br />
              If you are unsure, follow the link below to learn more about this role.
            </p>
          </InfoBody>
          <Typography fontSize="14px" margin="12px 0" style={{ color: palette.error.main }}>
            {error}
          </Typography>
          <Grid
            container
            direction={"row"}
            alignItems="center"
            justifyContent={"space-around"}
            sx={{
              borderBottom: "1px solid GrayText",
              pb: 8,
              width: "100%",
            }}
          >
            <InfoButton
              onClick={() => setClaiming(false)}
              // Colours only — the pill keeps InfoButton's 54px geometry. The theme's
              // contained+primary variant paints background/border from the palette and
              // inverts them on hover, so both have to be re-declared here; `&&` doubles
              // the class so this outranks the variant without an inline style.
              sx={{
                "&&": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  color: "black",
                  borderColor: "transparent",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    color: "black",
                    borderColor: "transparent",
                    boxShadow: "none",
                  },
                },
              }}
            >
              Cancel
            </InfoButton>
            <InfoButton
              onClick={() => handleClaimRequest()}
              color="primary"
              disabled={isRequestLoading}
              sx={{
                "&&": {
                  backgroundColor: "black",
                  color: "white",
                  borderColor: "black",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#333",
                    color: "white",
                    borderColor: "#333",
                    boxShadow: "none",
                  },
                  // The theme disables via `:disabled` (palette action.disabled); this
                  // repaints it to the popup spec's translucent black + dimmed label.
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                    color: "rgba(255, 255, 255, 0.6)",
                    borderColor: "transparent",
                  },
                },
              }}
            >
              {isRequestLoading ? "Loading" : "Confirm"}
            </InfoButton>
          </Grid>
          <p style={{ fontSize: "12px", color: "GrayText", marginTop: "16px" }}>
            <a
              href="/help?blog=blog-article-3"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginRight: "4px" }}
            >
              Here
            </a>
            is an in-depth article covering thie role and responsibilities of the Trustee.
          </p>
        </Wrapper>
      </DialogInfo>

      {/* reassign claim popup */}
      <DialogInfo open={isClaimingReassign} onClose={() => handleClaimReassignModalClose()} title={""}>
        <Wrapper
          sx={{
            p: 12,
            backgroundColor: "white",
            borderColor: ({ palette }) => palette.action.disabled,
          }}
        >
          <IconContainer>
            <CircularIcon>
              <InfoIconOutlined fontSize="large" color="primary" />
            </CircularIcon>
          </IconContainer>
          <InfoBody>
            <p style={{ fontWeight: 800, color: "black", alignSelf: "center", fontSize: "26px", marginTop: "16px" }}>
              Are you sure?
            </p>
            <p style={{ lineHeight: "1.5", textAlign: "center" }}>
              This action will opt your wallet into this land. By confirming, you accept to the role of Trustee to this
              land and the responsibilities it implies.
              <br />
              If you are unsure, follow the link below to learn more about this role.
            </p>
          </InfoBody>
          <Typography fontSize="14px" margin="12px 0" style={{ color: palette.error.main }}>
            {error}
          </Typography>
          <Grid
            container
            direction={"row"}
            alignItems="center"
            justifyContent={"space-around"}
            sx={{
              borderBottom: "1px solid GrayText",
              pb: 8,
              width: "100%",
            }}
          >
            {reassignLandIds.includes(selectedLand?.id) ? (
              <>
                <InfoButton
                  onClick={() => handleRejectReassignRequest()}
                  style={{ borderColor: "#999999", backgroundColor: "#999999" }}
                >
                  Reject
                </InfoButton>
                <InfoButton onClick={() => handleClaimReassignRequest()} color="primary" disabled={isRequestLoading}>
                  {isRequestLoading ? "Loading" : "Accept"}
                </InfoButton>
              </>
            ) : (
              <>
                <InfoButton
                  onClick={() => setClaimingReassign(false)}
                  style={{ borderColor: "#999999", backgroundColor: "#999999" }}
                >
                  Cancel
                </InfoButton>
                <InfoButton onClick={() => handleClaimUnassigned()} color="primary" disabled={isRequestLoading}>
                  {isRequestLoading ? "Loading" : "Opt In"}
                </InfoButton>
              </>
            )}
          </Grid>
          <p style={{ fontSize: "12px", color: "GrayText", marginTop: "16px" }}>
            <a
              href="/help?blog=blog-article-3"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginRight: "4px" }}
            >
              Here
            </a>
            is an in-depth article covering thie role and responsibilities of the Trustee.
          </p>
        </Wrapper>
      </DialogInfo>
    </Container>
  );
};

const Container = ({ children }: any) => (
  <Card
    sx={{
      p: sizing(0),
      pt: sizing(0),
      height: "100%",
      backgroundColor: "back.table",
      borderRadius: "20px",
    }}
  >
    {children}
  </Card>
);

// const Col = (props: GridProps) => <Grid {...props} container alignItems="center" direction="column" />;
// const Row = (props: GridProps) => <Grid {...props} container alignItems="center" direction="row" />;

// Every button in an Actions column takes one fixed height, so a cell that mixes text and
// icon-only buttons lines them up instead of letting each one's content set its size. The
// padding here is inner padding, horizontal only — the height is already fixed.
const TABLE_ACTION_HEIGHT = "36px";

const TableActionButton = styled(Button)({
  height: TABLE_ACTION_HEIGHT,
  minHeight: TABLE_ACTION_HEIGHT,
  padding: "0 16px",
  whiteSpace: "nowrap",
  backgroundColor: "black",
  color: "white",
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  border: "none",
  "&:hover": {
    backgroundColor: "#333",
    border: "none",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    color: "rgba(255, 255, 255, 0.6)",
  },
});

const DialogInfo = styled(MuiDialog)`
  .MuiDialog-paper {
    max-width: 40%;
    min-width: 300px;
    background-image: none;
    border-radius: 18px;
    //box-shadow: none;
    position: relative; /* Ensure child elements like IconContainer can be positioned */
    overflow: visible; /* Allow content to overflow */
    border: 1px solid ${({ theme }) => theme.palette.divider};
    @media (max-width: 560px) {
      // height:350px;
      margin: 12px;
    }
  }
`;

const InfoButton = styled(MuiButton)`
  height: 54px;
  margin-bottom: 10px;
  position: relative;
  border-color: none;
  &.Mui-disabled {
    img {
      opacity: 0.5;
    }
  }
  @media (max-width: 992px) {
    width: inherit;
    height: 54px;
  }
`;

const InfoBody = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  padding: 16px;

  p {
    font-size: 16px;
    font-family: Inter, sans-serif;
  }
  a {
    color: black;
    text-underline-offset: 2px;
    font-size: 16px;
    font-family: Inter, sans-serif;
  }
`;

const CircularIcon = styled("div")`
  background-color: #d1efff;
  padding: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px; /* Adjust the size as needed */
  height: 64px; /* Adjust the size as needed */
`;

const IconContainer = styled("div")`
  padding: 16px;
  position: absolute;
  top: -40px;
  border-radius: 100%;
  background-color: white;
  left: 50%;
  transform: translateX(-50%);
`;

const Wrapper = styled("div")`
  border-radius: 16px;
  width: fit-content;
  margin: auto;
  min-height: auto;
  max-height: fit-content;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;

  &.error {
    border-color: ${({ theme }) => theme.palette.error.main};
  }

  p {
    margin: 0;
    color: #3d3d3d;
  }

  button {
    margin-top: 10px;
    padding: 6px 42px;
  }

  &.cropping {
    width: 100%;
    height: auto;
  }
`;

export default TrustLands;
